import _ from 'lodash';
import React, { createContext, useCallback, useContext, useState } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  BankAccountWithInstitutionDesignationAndOwnerGQLType,
  ResolvedLoanFragmentGQLType,
  InvoiceInputGQLType,
  InvoiceItemInputGQLType,
  InvoiceComponentInputGQLType,
  InvoiceComponentWithInvoiceItemsInputGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { InvoiceFragmentGQLType } from 'packages/invoices/requests/invoices.gql.generated';
import { getUpdatedInvoiceTotal } from 'packages/invoices/ui/utils';
import { CreditFacility } from '~/contexts/CreditFacilitiesProvider';

type CreateInvoiceContextValue = {
  invoice: InvoiceInputGQLType;
  updateInvoice: ({
    field,
    value,
  }: {
    field: keyof InvoiceInputGQLType;
    value: any;
  }) => void;
  invoiceComponentsWithInvoiceItems: InvoiceComponentWithInvoiceItemsInputGQLType[];
  addInvoiceComponent: (params: InvoiceComponentInputGQLType) => void;
  updateInvoiceComponent: (params: {
    invoiceComponentIndex: number;
    field: keyof InvoiceComponentInputGQLType;
    value: any;
  }) => void;
  updateInvoiceItemsAndTotalsForInvoiceComponent: (params: {
    invoiceComponentIndex: number;
    newInvoiceItemsForInvoiceComponent: InvoiceItemInputGQLType[];
  }) => void;
  resetInvoiceComponents: () => void;
  resetInvoice: () => void;
  prefillInvoice: (params: {
    prefillInvoiceData: InvoiceFragmentGQLType;
    prefillCreditFacility: CreditFacility;
    prefillLoan: ResolvedLoanFragmentGQLType;
  }) => void;
  selectedEntities: {
    bankAccounts: {
      sourceBankAccount: BankAccountWithInstitutionDesignationAndOwnerGQLType | null;
      updateSourceBankAccount: React.Dispatch<
        React.SetStateAction<BankAccountWithInstitutionDesignationAndOwnerGQLType | null>
      >;
      destinationBankAccount: BankAccountWithInstitutionDesignationAndOwnerGQLType | null;
      updateDestinationBankAccount: React.Dispatch<
        React.SetStateAction<BankAccountWithInstitutionDesignationAndOwnerGQLType | null>
      >;
      maximumAvailableAmountCash: number | null;
      updateMaximumAvailableAmountCash: React.Dispatch<
        React.SetStateAction<number | null>
      >;
    };
    loan: {
      loan: ResolvedLoanFragmentGQLType | null;
      updateLoan: React.Dispatch<
        React.SetStateAction<ResolvedLoanFragmentGQLType | null>
      >;
    };
    creditFacility: {
      creditFacility: CreditFacility | null;
      updateCreditFacility: React.Dispatch<
        React.SetStateAction<CreditFacility | null>
      >;
    };
  };
};

const CreateInvoiceContext = createContext<CreateInvoiceContextValue | null>(
  null,
);

const initialInvoiceInputState: InvoiceInputGQLType = {
  creditFacilityId: '',
  finleyLoanId: null,
  originatingInvoiceId: null,
  paymentSourceOrganizationId: '',
  paymentDestinationOrganizationId: '',
  sourceBankAccountId: null,
  destinationBankAccountId: null,
  invoiceType: 'loan',
  status: 'created',
  totalAmount: 0,
  autoSettle: true,
  memo: null,
  accrualStartDate: commonDate.currentDateStr(),
  accrualEndDate: commonDate.currentDateStr(),
  targetInvoiceDate: null,
  dueDate: commonDate.currentDateStr(),
  paymentDate: null,
};

const getPrefilledInvoiceInputState = (
  prefillInvoiceData: InvoiceFragmentGQLType,
): {
  invoice: InvoiceInputGQLType;
  invoiceComponentsWithInvoiceItems: InvoiceComponentWithInvoiceItemsInputGQLType[];
} => {
  const {
    id: originatingInvoiceId,
    creditFacilityId,
    finleyLoanId,
    paymentSourceOrganizationId,
    paymentDestinationOrganizationId,
    sourceBankAccountId,
    destinationBankAccountId,
    invoiceType,
    totalAmount,
    autoSettle,
    memo,
    accrualStartDate,
    accrualEndDate,
    targetInvoiceDate,
    dueDate,
    invoiceComponentsWithInvoiceItems,
  } = prefillInvoiceData;

  const newInvoiceComponentsWithInvoiceItems: InvoiceComponentWithInvoiceItemsInputGQLType[] = invoiceComponentsWithInvoiceItems.map(
    (invoiceComponent) => {
      const {
        invoiceComponent: component,
        invoiceItems: items,
      } = invoiceComponent;

      const {
        finleyLoanId: componentFinleyLoanId,
        invoiceComponentType: componentInvoiceComponentType,
        accrualStartDate: componentAccrualStartDate,
        accrualEndDate: componentAccrualEndDate,
        totalAmount: componentTotalAmount,
      } = component;

      const newInvoiceComponent: InvoiceComponentInputGQLType = {
        finleyLoanId: componentFinleyLoanId,
        invoiceComponentType: componentInvoiceComponentType,
        accrualStartDate: componentAccrualStartDate,
        accrualEndDate: componentAccrualEndDate,
        totalAmount: componentTotalAmount,
      };

      const newInvoiceItems: InvoiceItemInputGQLType[] = items.map((item) => ({
        accountingAccountId: item.accountingAccountId,
        amount: item.amount,
        status: 'created' as const,
        capitalizedLoanId: item.capitalizedLoanId,
        accountingAccount: {
          id: item.accountingAccount.id,
          tAccount: item.accountingAccount.tAccount,
          organizationId: item.accountingAccount.organizationId,
          creditFacilityId: item.accountingAccount.creditFacilityId,
        },
      }));

      return {
        invoiceComponent: newInvoiceComponent,
        invoiceItems: newInvoiceItems,
      };
    },
  );

  return {
    invoice: {
      creditFacilityId,
      finleyLoanId,
      originatingInvoiceId,
      paymentSourceOrganizationId,
      paymentDestinationOrganizationId,
      sourceBankAccountId,
      destinationBankAccountId,
      invoiceType,
      status: 'created',
      totalAmount,
      autoSettle,
      memo,
      accrualStartDate,
      accrualEndDate,
      targetInvoiceDate,
      dueDate,
      paymentDate: null,
    },
    invoiceComponentsWithInvoiceItems: newInvoiceComponentsWithInvoiceItems,
  };
};

export const useCreateInvoiceContext = () => {
  const context = useContext(CreateInvoiceContext);

  if (!context) {
    throw new Error(
      'CreateInvoiceProvider must be present to use CreateInvoiceContext',
    );
  }

  return context;
};

export const CreateInvoiceProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [invoice, setInvoice] = useState<InvoiceInputGQLType>(
    initialInvoiceInputState,
  );
  const [
    invoiceComponentsWithInvoiceItems,
    setInvoiceComponentsWithInvoiceItems,
  ] = useState<InvoiceComponentWithInvoiceItemsInputGQLType[]>([]);

  // some entities are used across multiple pages on the guided workflow
  // helpful to store them in context instead of making calls on every page to fetch
  const [
    selectedSourceBankAccount,
    setSelectedSourceBankAccount,
  ] = useState<BankAccountWithInstitutionDesignationAndOwnerGQLType | null>(
    null,
  );
  const [
    selectedDestinationBankAccount,
    setSelectedDestinationBankAccount,
  ] = useState<BankAccountWithInstitutionDesignationAndOwnerGQLType | null>(
    null,
  );
  const [maximumAvailableAmountCash, setMaximumAvailableAmountCash] = useState<
    number | null
  >(null);
  const [
    selectedLoan,
    setSelectedLoan,
  ] = useState<ResolvedLoanFragmentGQLType | null>(null);
  const [
    selectedCreditFacility,
    setSelectedCreditFacility,
  ] = useState<CreditFacility | null>(null);

  const updateInvoice = ({
    field,
    value,
  }: {
    field: keyof InvoiceInputGQLType;
    value: any;
  }) => {
    setInvoice((prevInvoice) => ({
      ...prevInvoice,
      [field]: value,
    }));
  };

  const addInvoiceComponent = ({
    invoiceComponentType,
    finleyLoanId,
    accrualStartDate,
    accrualEndDate,
  }: InvoiceComponentInputGQLType) => {
    setInvoiceComponentsWithInvoiceItems(
      (prevInvoiceComponentsWithInvoiceItems) => [
        ...prevInvoiceComponentsWithInvoiceItems,
        {
          invoiceComponent: {
            totalAmount: 0,
            invoiceComponentType,
            finleyLoanId,
            accrualStartDate,
            accrualEndDate,
          },
          invoiceItems: [],
        },
      ],
    );
  };

  const updateInvoiceComponent = ({
    invoiceComponentIndex,
    field,
    value,
  }: {
    invoiceComponentIndex: number;
    field: keyof InvoiceComponentInputGQLType;
    value: any;
  }) => {
    const newInvoiceComponentsWithInvoiceItems = [
      ...invoiceComponentsWithInvoiceItems,
    ];

    newInvoiceComponentsWithInvoiceItems[
      invoiceComponentIndex
    ].invoiceComponent = {
      ...newInvoiceComponentsWithInvoiceItems[invoiceComponentIndex]
        .invoiceComponent,
      [field]: value,
    };

    setInvoiceComponentsWithInvoiceItems(newInvoiceComponentsWithInvoiceItems);
  };

  /**
   * Updates the invoice items and totals for a given invoice component
   * The invoice component totalAmount and the invoice totalAmount are updated
   */
  const updateInvoiceItemsAndTotalsForInvoiceComponent = ({
    newInvoiceItemsForInvoiceComponent,
    invoiceComponentIndex,
  }: {
    newInvoiceItemsForInvoiceComponent: InvoiceItemInputGQLType[];
    invoiceComponentIndex: number;
  }) => {
    const newInvoiceComponentsWithInvoiceItems = [
      ...invoiceComponentsWithInvoiceItems,
    ];

    newInvoiceComponentsWithInvoiceItems[
      invoiceComponentIndex
    ].invoiceItems = newInvoiceItemsForInvoiceComponent;

    setInvoiceComponentsWithInvoiceItems(newInvoiceComponentsWithInvoiceItems);

    const newInvoiceComponentTotal = commonUtils.jsRound({
      value: _.sumBy(newInvoiceItemsForInvoiceComponent, (item) => item.amount),
      precision: 2,
    });
    updateInvoiceComponent({
      invoiceComponentIndex,
      field: 'totalAmount',
      value: newInvoiceComponentTotal,
    });

    const newInvoiceTotal = getUpdatedInvoiceTotal({
      invoiceComponentsWithInvoiceItems,
      updatedInvoiceComponentTotal: newInvoiceComponentTotal,
      invoiceComponentIndex,
    });
    updateInvoice({ field: 'totalAmount', value: newInvoiceTotal });
  };

  const resetInvoiceComponents = () => {
    setInvoiceComponentsWithInvoiceItems([]);
  };

  const resetInvoice = () => {
    // reset invoice
    setInvoice(initialInvoiceInputState);
    setInvoiceComponentsWithInvoiceItems([]);

    // reset entities
    setSelectedSourceBankAccount(null);
    setSelectedDestinationBankAccount(null);
    setMaximumAvailableAmountCash(null);
    setSelectedLoan(null);
    setSelectedCreditFacility(null);
  };

  const prefillInvoice = useCallback(
    ({
      prefillInvoiceData,
      prefillCreditFacility,
      prefillLoan,
    }: {
      prefillInvoiceData: InvoiceFragmentGQLType;
      prefillCreditFacility: CreditFacility;
      prefillLoan: ResolvedLoanFragmentGQLType;
    }) => {
      if (!prefillInvoiceData) {
        return;
      }

      resetInvoice(); // first reset any existing create invoice context

      const prefilledInvoiceInputState = getPrefilledInvoiceInputState(
        prefillInvoiceData,
      );
      setInvoice(prefilledInvoiceInputState.invoice);
      setInvoiceComponentsWithInvoiceItems(
        prefilledInvoiceInputState.invoiceComponentsWithInvoiceItems,
      );

      // update selected entities
      setSelectedCreditFacility(prefillCreditFacility ?? null);
      setSelectedLoan(prefillLoan ?? null);
    },
    [],
  );

  return (
    <CreateInvoiceContext.Provider
      value={{
        invoice,
        updateInvoice,
        invoiceComponentsWithInvoiceItems,
        addInvoiceComponent,
        updateInvoiceComponent,
        updateInvoiceItemsAndTotalsForInvoiceComponent,
        resetInvoiceComponents,
        resetInvoice,
        prefillInvoice,
        selectedEntities: {
          bankAccounts: {
            sourceBankAccount: selectedSourceBankAccount,
            updateSourceBankAccount: setSelectedSourceBankAccount,
            destinationBankAccount: selectedDestinationBankAccount,
            updateDestinationBankAccount: setSelectedDestinationBankAccount,
            maximumAvailableAmountCash,
            updateMaximumAvailableAmountCash: setMaximumAvailableAmountCash,
          },
          loan: { loan: selectedLoan, updateLoan: setSelectedLoan },
          creditFacility: {
            creditFacility: selectedCreditFacility,
            updateCreditFacility: setSelectedCreditFacility,
          },
        },
      }}
    >
      {children}
    </CreateInvoiceContext.Provider>
  );
};
